import type { ChangeEvent } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import LineBugIcon from 'LuxKit/Icons/line/LineBugIcon'
import LineChatBubbleUserIcon from 'LuxKit/Icons/line/LineChatBubbleUserIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ContactContext from 'contexts/contactContext'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import type { BasicTrip } from 'tripPlanner/types/common'
import Switch from 'LuxKit/Switch/Switch'
import Sheet from 'components/utils/Sheet'
import { buildRecentChatItems, type RecentChatItem } from 'tripPlanner/pages/chat/recentChatItems'
import DrawerRecentChatListItem from './DrawerRecentChatListItem'
import { getTripURL } from 'tripPlanner/utils'
import useUserMessageSubtitles from './useUserMessageSubtitles'

const FEEDBACK_SURVEY_URL = 'https://usabi.li/do/gizugyzacokd/vj9hxb'

const HeaderLogoWrapper = styled.div`
  display: flex;
  align-items: center;
`

const DrawerModalBody = styled(ModalBody)`
  overflow: hidden;
  min-height: 0;
`

const DrawerModalContent = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  min-height: 0;
`

const DrawerLayout = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(12)};
  min-height: 0;
`

const ChatsSection = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(16)};
  min-height: 0;
`

const ChatHistoryList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
`

const DrawerBottomControls = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: ${rem(28)};
`

interface Props {
  onStartNewChat: () => void
  recentChats?: Array<TripPlannerChat>
  recentTrips?: Array<BasicTrip>
  onChatSelect?: (chatId: number) => void
  onChatRename?: (chatId: number, name: string) => Promise<void>
  onTripRename?: (tripId: string, name: string) => Promise<void>
  onChatDelete?: (chatId: number) => void
  onTripDelete?: (tripId: string) => void
  // Staff tools
  isStaff?: boolean
  isDevMode?: boolean
  onDevModeToggle?: () => void
}

function TripPlannerDrawer({
  onStartNewChat,
  recentChats,
  recentTrips,
  onChatSelect,
  onChatRename,
  onTripRename,
  onChatDelete,
  onTripDelete,
  isStaff = false,
  isDevMode = false,
  onDevModeToggle,
}: Props) {
  const intl = useIntl()
  const { resolve } = useModalElementContext()
  const supportContact = useContext(ContactContext)

  const closeDrawer = useCallback(() => {
    resolve()
  }, [resolve])

  const handleNewChat = useCallback(() => {
    closeDrawer()
    onStartNewChat()
  }, [closeDrawer, onStartNewChat])

  const handleRecentItemClick = useCallback((item: RecentChatItem) => () => {
    closeDrawer()
    if (item.type === 'aiChat') {
      onChatSelect?.(item.id)
    }
  }, [closeDrawer, onChatSelect])

  const handleDeleteClick = useCallback(
    (item: RecentChatItem, closeMenu: () => void) => (event: React.MouseEvent) => {
      event.stopPropagation()
      closeMenu()
      closeDrawer()
      if (item.type === 'aiChat') {
        onChatDelete?.(item.id)
        return
      }
      onTripDelete?.(item.id)
    },
    [closeDrawer, onChatDelete, onTripDelete],
  )

  const handleRename = useCallback(
    async(item: RecentChatItem, name: string) => {
      if (item.type === 'aiChat') {
        await onChatRename?.(item.id, name)
        return
      }
      await onTripRename?.(item.id, name)
    },
    [onChatRename, onTripRename],
  )

  const handleDevModeToggle = useCallback((_event: ChangeEvent<HTMLInputElement>) => {
    closeDrawer()
    onDevModeToggle?.()
  }, [closeDrawer, onDevModeToggle])

  const allRecentItems = useMemo(() => {
    return buildRecentChatItems(recentChats ?? [], recentTrips ?? [])
  }, [recentChats, recentTrips])
  const hasRecentItems = allRecentItems.length > 0
  const visibleAiChatIds = useMemo(() => {
    return allRecentItems
      .filter(item => item.type === 'aiChat')
      .map(item => item.id)
  }, [allRecentItems])
  const userMessageSubtitleByChatId = useUserMessageSubtitles(visibleAiChatIds)
  const chatsLabel = intl.formatMessage({
    id: 'x3JBSvTY-jXIoAlF0Kiw',
    defaultMessage: 'Chats',
    description: 'Section heading for the chats list in the trip planner drawer',
  })
  const createNewChatLabel = intl.formatMessage({
    id: 'hMLFrteXDG4txmeo29R7',
    defaultMessage: 'Create new chat',
    description: 'Button label to start a new chat in the trip planner drawer',
  })
  const planWithAiChatHeading = intl.formatMessage({
    id: 'LBjr7kIwyT6PI1I_-koY',
    defaultMessage: 'Plan with AI chat',
    description: 'Heading for the empty state when no chats exist in the drawer',
  })
  const planWithAiChatBody = intl.formatMessage({
    id: 'w3ssyhVL6Z7hEsPnhNhc',
    defaultMessage: 'Explore ideas, shape your trip, and refine plans as you go.',
    description: 'Description for the empty state when no chats exist in the drawer',
  })
  const giveFeedbackTitle = intl.formatMessage({
    id: 'XQlLVgnHoOF0Vvfrs3F5',
    defaultMessage: 'Give feedback',
    description: 'Title for the give feedback link in the TripPlanner navigation drawer menu',
  })

  const giveFeedbackSubtitle = intl.formatMessage({
    id: 'EFHzgukGnljGQ2-yKUwj',
    defaultMessage: 'We want to hear your thoughts on TripPlanner',
    description: 'Subtitle encouraging users to share feedback about the TripPlanner feature',
  })

  const needSupportTitle = intl.formatMessage({
    id: 'FbRO4l6WxkGkA3OmUAPm',
    defaultMessage: 'Need support? Call us 24/7',
    description: 'Title for the customer support link in the TripPlanner navigation drawer',
  })

  const staffToolsText = intl.formatMessage({
    id: 'MVS8v_NHiDbWbR0NuItv',
    defaultMessage: 'Staff tools',
    description: 'Label for the staff-only tools section in the TripPlanner navigation drawer',
  })

  const devModeText = intl.formatMessage({
    id: 'dXMxtVabgKHFm0Fe0oMH',
    defaultMessage: 'Dev mode',
    description: 'Toggle label for enabling developer mode in the TripPlanner navigation drawer',
  })

  const headerTitle = useMemo(() => (
    <HeaderLogoWrapper>
      <Clickable to="">
        <LogoIcon height={24} desktopVariant="icon+text" />
      </Clickable>
    </HeaderLogoWrapper>
  ), [])

  return (
    <ModalBase mode="menu">
      <ModalHeader title={headerTitle} onCloseButtonClick={closeDrawer} />
      <DrawerModalBody>
        <DrawerModalContent flushListItems>
          <DrawerLayout>
            <ChatsSection>
              <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="center">
                <BodyText variant="small" colour="neutral-three">
                  {chatsLabel}
                </BodyText>
                <TextButton kind="tertiary" size="small" onClick={handleNewChat}>
                  {createNewChatLabel}
                </TextButton>
              </Group>

              {hasRecentItems && (
                <ChatHistoryList>
                  {allRecentItems.map(item => (
                    <DrawerRecentChatListItem
                      key={`${item.type}-${item.id}`}
                      item={item}
                      subtitle={item.type === 'aiChat' ? userMessageSubtitleByChatId.get(item.id) : item.subtitle}
                      to={item.type === 'classicTrip' ? getTripURL(item.id) : undefined}
                      onClick={handleRecentItemClick(item)}
                      onRename={handleRename}
                      onDeleteClick={handleDeleteClick}
                    />
                  ))}
                </ChatHistoryList>
              )}
              {!hasRecentItems && (
                <Sheet padding="12 20" background="neutral-seven" borderRadius="M">
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="medium" weight="bold">
                      {planWithAiChatHeading}
                    </BodyText>
                    <BodyText variant="small" colour="neutral-two">
                      {planWithAiChatBody}
                    </BodyText>
                  </Group>
                </Sheet>
              )}
            </ChatsSection>

            <DrawerBottomControls>
              <Group direction="vertical" gap={8}>
                <ClickableListItem
                  title={giveFeedbackTitle}
                  subtitle={giveFeedbackSubtitle}
                  startIcon={<LineChatBubbleUserIcon />}
                  href={FEEDBACK_SURVEY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                />
                <ClickableListItem
                  title={needSupportTitle}
                  subtitle={supportContact.local.humanReadable}
                  startIcon={<LinePhoneIcon />}
                  href={`tel:${supportContact.local.number}`}
                />
              </Group>
              {isStaff && (
                <Group direction="vertical" gap={12}>
                  <BodyText variant="medium" weight="bold">{staffToolsText}</BodyText>

                  <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
                    <Group direction="horizontal" gap={8} verticalAlign="center">
                      <LineBugIcon />
                      <BodyText variant="medium">{devModeText}</BodyText>
                    </Group>
                    <Switch
                      checked={isDevMode}
                      onChange={handleDevModeToggle}
                    />
                  </Group>
                </Group>
              )}
            </DrawerBottomControls>
          </DrawerLayout>
        </DrawerModalContent>
      </DrawerModalBody>
    </ModalBase>
  )
}

export default TripPlannerDrawer
